import { useEffect, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { AppBootLoading } from '../components/AppBootLoading'
import { registerAnalyticsSession, trackPageViewIfChanged } from '../platform/analytics/analytics'
import { trackNavigationRedirected } from '../platform/analytics/interactionAnalytics'
import { useAbp } from '../platform/abp/react'
import { SCENARIOS_SCREEN } from '../scenarios/scenariosAnalytics'
import { ScenariosPage } from '../scenarios/ScenariosPage'
import {
  parseStudioSessionId,
  parseStudioTemplateId,
  readStudioBoardQuery,
  clearPostLoginStudioPath,
  rememberStudioArrival,
  rememberStudioBoard,
  rememberStudioTemplate,
  rememberPostLoginStudioPath,
  SCENARIOS_PATH,
  SCENARIOS_TEMPLATES_PATH,
  studioSessionPath,
  studioTemplatePath,
} from '../scenarios/serverMode'
import { HOME_PATH, LOGIN_PATH } from './pathPolicy'

type LoginLocationState = {
  returnTo?: string
}

/**
 * Auth-gated Scenario Studio. Requires Buxus.Scenarios and Buxus.Scenarios.View.
 * URLs: `/scenarios`, `/scenarios/<uuid>`, `/scenarios/templates`, `/scenarios/templates/<id>`.
 */
export function ScenariosPocRoute() {
  const location = useLocation()
  const { ready, isAuthenticated, featureEnabled, can } = useAbp()
  const studioFeatureOn = featureEnabled('Buxus.Scenarios')
  const canViewStudio = can('Buxus.Scenarios.View')

  const deniedRedirectSent = useRef(false)
  const sessionId = parseStudioSessionId(location.pathname) || readStudioBoardQuery(location.search)
  const templateId = parseStudioTemplateId(location.pathname)
  const fromQuery = readStudioBoardQuery(location.search)
  const fromSessionPath = parseStudioSessionId(location.pathname)

  // Only when the deep-link target changes — not every render (avoids arrival thrash).
  useEffect(() => {
    if (templateId) {
      rememberStudioTemplate(templateId)
      rememberStudioArrival('template', templateId)
      return
    }
    if (sessionId) {
      rememberStudioBoard(sessionId)
      rememberStudioArrival('board', sessionId)
    }
  }, [sessionId, templateId])

  useEffect(() => {
    registerAnalyticsSession({ sessionId })
    return () => registerAnalyticsSession({ sessionId: null })
  }, [sessionId])

  useEffect(() => {
    if (!studioFeatureOn || !canViewStudio || !ready || !isAuthenticated) return
    const path = templateId
      ? studioTemplatePath(templateId)
      : sessionId
        ? studioSessionPath(sessionId)
        : location.pathname.startsWith(SCENARIOS_TEMPLATES_PATH)
          ? SCENARIOS_TEMPLATES_PATH
          : SCENARIOS_PATH
    registerAnalyticsSession({ sessionId })
    trackPageViewIfChanged(sessionId ? `${SCENARIOS_SCREEN}|${sessionId}` : SCENARIOS_SCREEN, {
      screen: 'Scenarios',
      screenConstant: SCENARIOS_SCREEN,
      isScenarios: true,
      routePath: path,
      ...(sessionId ? { sessionId } : {}),
    })
  }, [studioFeatureOn, canViewStudio, ready, isAuthenticated, sessionId])

  if (!ready) {
    return <AppBootLoading label="Loading…" />
  }

  if (!isAuthenticated) {
    if (sessionId) rememberPostLoginStudioPath(sessionId)
    const returnTo = templateId
      ? studioTemplatePath(templateId)
      : sessionId
        ? studioSessionPath(sessionId)
        : location.pathname.startsWith(SCENARIOS_TEMPLATES_PATH)
          ? SCENARIOS_TEMPLATES_PATH
          : SCENARIOS_PATH
    return (
      <Navigate
        to={LOGIN_PATH}
        replace
        state={{ returnTo } satisfies LoginLocationState}
      />
    )
  }

  clearPostLoginStudioPath()

  if (!studioFeatureOn || !canViewStudio) {
    if (!deniedRedirectSent.current) {
      deniedRedirectSent.current = true
      trackNavigationRedirected({
        screen: 'Scenarios',
        screenConstant: SCENARIOS_SCREEN,
        isScenarios: true,
        reason: studioFeatureOn ? 'scenarios_view_denied' : 'scenarios_feature_off',
        fromScreen: 'Scenarios',
        toScreen: 'Home',
        fromScreenConstant: SCENARIOS_SCREEN,
        toScreenConstant: 'OPERATOR_SEARCH_FORM_SCREEN',
        ...(sessionId ? { sessionId } : {}),
      })
    }
    return <Navigate to={HOME_PATH} replace />
  }

  // Legacy `?board=` → canonical session path.
  if (fromQuery && !fromSessionPath && !templateId) {
    return <Navigate to={studioSessionPath(fromQuery)} replace />
  }

  return <ScenariosPage />
}
